import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Database, Activity, Zap, Server, AlertTriangle, Info, CheckCircle, AlertOctagon, GitBranch, Tag } from 'lucide-react';
import { AppState, Question, GroupId } from '../../types';
import { QuestionBrowser } from './QuestionBrowser';
import { ValidationPanel } from './ValidationPanel';
import { SkillTreePanel } from './SkillTreePanel';
import { FamilyIdExplorer } from './FamilyIdExplorer';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
interface DeveloperPageProps {
  state: AppState;
  questions: Question[];
  selectedGroup: GroupId;
  onJumpToLevel: (level: number) => void;
  onResetProgress: () => void;
}
type DevTab = 'browser' | 'validation' | 'taxonomy' | 'families' | 'actions' | 'system';
const tabs: {
  id: DevTab;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
}[] = [{
  id: 'browser',
  label: 'Vraag Browser',
  shortLabel: 'Browser',
  icon: Database
}, {
  id: 'taxonomy',
  label: 'Taxonomie',
  shortLabel: 'Taxonomie',
  icon: GitBranch
}, {
  id: 'families',
  label: 'Families',
  shortLabel: 'Families',
  icon: Tag
}, {
  id: 'validation',
  label: 'Validatie',
  shortLabel: 'Validatie',
  icon: Activity
}, {
  id: 'actions',
  label: 'Acties',
  shortLabel: 'Acties',
  icon: Zap
}, {
  id: 'system',
  label: 'Systeem',
  shortLabel: 'Systeem',
  icon: Server
}];
export function DeveloperPage({
  state,
  questions,
  selectedGroup,
  onJumpToLevel,
  onResetProgress
}: DeveloperPageProps) {
  const [activeTab, setActiveTab] = useState<DevTab>('browser');
  return <div className="max-w-7xl mx-auto" data-id="element-568">
      {/* Header with inline stats */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-569">
        <div className="flex items-center gap-4" data-id="element-570">
          <div className="p-2.5 bg-slate-900 rounded-xl text-white shadow-lg shadow-slate-900/20" data-id="element-571">
            <Terminal className="w-5 h-5" data-id="element-572" />
          </div>
          <div data-id="element-573">
            <h1 className="text-xl font-bold text-slate-900" data-id="element-574">
              Developer Console
            </h1>
            <p className="text-slate-500 text-sm" data-id="element-575">
              Geavanceerde tools voor beheer en debugging
            </p>
          </div>
        </div>
        {/* Inline session stats */}
        <div className="flex items-center gap-3 flex-wrap" data-id="element-576">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 text-white rounded-lg text-xs font-mono" data-id="element-577">
            <span className="text-slate-400" data-id="element-578">Groep</span>
            <span className="text-indigo-400 font-bold" data-id="element-579">{selectedGroup}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 text-white rounded-lg text-xs font-mono" data-id="element-580">
            <span className="text-slate-400" data-id="element-581">Vragen</span>
            <span className="text-emerald-400 font-bold" data-id="element-582">
              {questions.length}
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 text-white rounded-lg text-xs font-mono" data-id="element-583">
            <span className="text-slate-400" data-id="element-584">v2.0.0</span>
          </span>
        </div>
      </div>

      {/* Horizontal Tab Bar */}
      <div className="flex gap-1 mb-4 bg-white rounded-xl border border-slate-200 p-1 overflow-x-auto" data-id="element-585">
        {tabs.map(tab => {
        const isActive = activeTab === tab.id;
        return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap flex-1 justify-center min-w-0
                ${isActive ? 'text-white' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}
              `} data-id="element-586">
              {isActive && <motion.div layoutId="dev-active-tab" className="absolute inset-0 bg-slate-900 rounded-lg shadow-md" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-587" />}
              <tab.icon className={`w-4 h-4 relative z-10 flex-shrink-0 ${isActive ? 'text-indigo-400' : ''}`} data-id="element-588" />
              <span className="relative z-10 hidden sm:inline" data-id="element-589">
                {tab.label}
              </span>
              <span className="relative z-10 sm:hidden" data-id="element-590">{tab.shortLabel}</span>
            </button>;
      })}
      </div>

      {/* Content Area — full width, scrolls naturally */}
      <AnimatePresence mode="wait" data-id="element-591">
        <motion.div key={activeTab} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.15
      }} data-id="element-592">
          {activeTab === 'browser' && <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{
          minHeight: 'calc(100vh - 16rem)'
        }} data-id="element-593">
              <div className="h-[calc(100vh-16rem)] flex flex-col" data-id="element-594">
                <QuestionBrowser questions={questions} data-id="element-595" />
              </div>
            </div>}

          {activeTab === 'taxonomy' && <SkillTreePanel questions={questions} data-id="element-596" />}

          {activeTab === 'families' && <FamilyIdExplorer questions={questions} data-id="element-597" />}

          {activeTab === 'validation' && <Card className="p-6" data-id="element-598">
              <ValidationPanel questions={questions} data-id="element-599" />
            </Card>}

          {activeTab === 'actions' && <ActionsTab onJumpToLevel={onJumpToLevel} onResetProgress={onResetProgress} data-id="element-600" />}

          {activeTab === 'system' && <SystemTab state={state} selectedGroup={selectedGroup} data-id="element-601" />}
        </motion.div>
      </AnimatePresence>
    </div>;
}
function ActionsTab({
  onJumpToLevel,
  onResetProgress
}: {
  onJumpToLevel: (l: number) => void;
  onResetProgress: () => void;
}) {
  return <div className="max-w-2xl space-y-6" data-id="element-602">
      <Card className="p-6" data-id="element-603">
        <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2" data-id="element-604">
          <Zap className="w-5 h-5 text-amber-500" data-id="element-605" />
          Level Manipulation
        </h3>
        <p className="text-slate-500 text-sm mb-4" data-id="element-606">
          Spring naar een specifiek level om progressie-logica te testen.
        </p>
        <div className="flex flex-wrap gap-3" data-id="element-607">
          {[5, 10, 20, 50, 100].map(level => <Button key={level} onClick={() => onJumpToLevel(level)} variant="secondary" size="sm" data-id="element-608">
              Level {level}
            </Button>)}
        </div>
      </Card>

      <Card className="p-6 border-rose-200 bg-rose-50/30" data-id="element-609">
        <div className="flex items-start gap-4" data-id="element-610">
          <div className="p-2.5 bg-rose-100 rounded-xl text-rose-600 flex-shrink-0" data-id="element-611">
            <AlertOctagon className="w-5 h-5" data-id="element-612" />
          </div>
          <div className="flex-1" data-id="element-613">
            <h3 className="font-bold text-rose-900 flex items-center gap-2" data-id="element-614">
              <AlertTriangle className="w-4 h-4" data-id="element-615" />
              Danger Zone
            </h3>
            <p className="text-rose-700/80 text-sm mt-1 mb-4" data-id="element-616">
              Verwijdert permanent alle lokale data: XP, level en
              vraaggeschiedenis. Kan niet ongedaan worden.
            </p>
            <Button onClick={onResetProgress} variant="ghost" size="sm" className="bg-white border border-rose-200 text-rose-600 hover:bg-rose-600 hover:text-white" data-id="element-617">
              Reset Everything
            </Button>
          </div>
        </div>
      </Card>
    </div>;
}
function SystemTab({
  state,
  selectedGroup
}: {
  state: AppState;
  selectedGroup: GroupId;
}) {
  const totalQuestions = Object.values(state.progress).reduce((sum, mod) => sum + mod.questionsAnswered.length, 0);
  const stats = [{
    label: 'App Version',
    value: 'v2.0.0',
    icon: Terminal
  }, {
    label: 'Selected Group',
    value: `Groep ${selectedGroup}`,
    icon: Server
  }, {
    label: 'Total XP',
    value: state.stats.xp.toLocaleString(),
    icon: Zap
  }, {
    label: 'Questions Answered',
    value: totalQuestions.toLocaleString(),
    icon: Activity
  }];
  return <div className="space-y-6" data-id="element-618">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-619">
        {stats.map(stat => <Card key={stat.label} className="p-4" data-id="element-620">
            <div className="flex items-center gap-3" data-id="element-621">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-500" data-id="element-622">
                <stat.icon className="w-4 h-4" data-id="element-623" />
              </div>
              <div className="min-w-0" data-id="element-624">
                <div className="text-xs text-slate-400 font-medium truncate" data-id="element-625">
                  {stat.label}
                </div>
                <div className="text-lg font-bold text-slate-900" data-id="element-626">
                  {stat.value}
                </div>
              </div>
            </div>
          </Card>)}
      </div>

      <Card className="p-6" data-id="element-627">
        <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-628">
          <Info className="w-4 h-4 text-indigo-500" data-id="element-629" />
          Storage Status
        </h4>
        <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg border border-emerald-100 mb-4" data-id="element-630">
          <CheckCircle className="w-4 h-4" data-id="element-631" />
          <span className="font-medium text-sm" data-id="element-632">Local Storage Active</span>
        </div>
        <pre className="text-xs text-slate-500 font-mono bg-slate-50 p-4 rounded-lg overflow-x-auto border border-slate-100" data-id="element-633">
          {JSON.stringify({
          storageKey: 'cito_meester_djayden_v2',
          usage: 'Local Browser Storage',
          persisted: true
        }, null, 2)}
        </pre>
      </Card>
    </div>;
}